import { IVoucher, RootState } from '@checkout/state';
import * as cx from 'classnames';
import dayjs from 'dayjs';
import * as React from 'react';
import { ChangeEvent, Fragment, KeyboardEvent } from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { CheckoutContextConfig } from '../../../apps/CheckoutApp';
import { voucherTypes } from '../../../config/contextCheckoutConfig';
import messages from '../../../i18n/base-en';
import { addVoucherRequest, removeVoucherRequest } from '../../../reducers/checkout';
import { Button, FieldMessage, InputBox, MessageBox, TextInput, VoucherList } from '../../presentational';
import * as Styles from './Voucher.scss';

interface IOwnProps {
  id: string;
  labelSize?: 'big';
  isDefaultActive: boolean;
  isDefaultActiveOnMobile: boolean;
  isMobile: boolean;
}

interface IConnectedProps {
  vouchers: IVoucher[];
  voucherError?: string;
  isVoucherFetching: boolean;
  voucherLockExpireDate: undefined | Date;
}

interface IConnectetDispatchProps {
  addVoucherRequest: typeof addVoucherRequest;
  removeVoucherRequest: typeof removeVoucherRequest;
}

type Props = IOwnProps & IConnectedProps & IConnectetDispatchProps & InjectedIntlProps;

interface IOwnState {
  isActive: boolean;
  isBlocked: boolean;
  isPristine: boolean;
  value: string;
}

class Voucher extends React.Component<Props, IOwnState> {
  state: IOwnState = {
    isActive: (this.props.isDefaultActiveOnMobile && this.props.isMobile)
      || (this.props.isDefaultActive && !this.props.isMobile) || false,
    isBlocked: false,
    isPristine: true,
    value: '',
  };

  componentDidMount() {
    this.updateVoucherBlockedState();

    // Check for each 2 seconds
    setInterval(() => {
      this.updateVoucherBlockedState();
    }, 2000);
  }

  updateVoucherBlockedState = () => {
    this.setState({
      isBlocked: this.isVoucherBlocked(),
    });
  }

  toggleActive = () => {
    this.setState({
      isActive: !this.state.isActive,
      isPristine: false,
    });
  }

  handleChange = (event: ChangeEvent<any>) => {
    const { value } = event.target;

    this.setState({
      value,
    });
  }

  addVoucher = () => {
    this.props.addVoucherRequest(this.state.value);
    this.setState({
      value: '',
    });
  }

  // Adding voucher on Enter keyboard key press
  handleKeyboardKeypress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && this.state.value && this.state.value !== '') {
      event.preventDefault();
      this.addVoucher();
    }
  }

  removeVoucher = (value: string) => {
    this.props.removeVoucherRequest(value);
  }

  isVoucherBlocked = () => {
    const { voucherLockExpireDate } = this.props;
    const today = dayjs();

    return voucherLockExpireDate !== undefined && today.isSameOrBefore(voucherLockExpireDate);
  }

  render() {
    const { id, vouchers, voucherError, labelSize, intl, isVoucherFetching, isDefaultActive } = this.props;
    const { isActive, isBlocked, value } = this.state;
    const { formatMessage } = intl;

    const voucherToggleClassNames = cx({
      [Styles.toggleActive]: isActive,
    });

    return (
      <CheckoutContextConfig.Consumer>
        {(checkoutContextConfig => (
          <div className={Styles.wrapper}>
            {checkoutContextConfig.ui.voucher.type === voucherTypes.link && !isActive && (
              <div className={Styles.discountButton} onClick={this.toggleActive}>
                {formatMessage(messages.voucher_switch_button_label)}
              </div>
            )}
            {checkoutContextConfig.ui.voucher.type === voucherTypes.toggle && (
              <div className={voucherToggleClassNames}>
                <InputBox
                  id="voucher_switch_button"
                  name="voucher_switch_button"
                  type="checkbox"
                  checked={isActive}
                  value={isActive}
                  onChange={this.toggleActive}
                  variant="toggle"
                  sizeVariant={labelSize}
                  label={formatMessage(messages.voucher_switch_button_label)}
                />
              </div>
            )}
            {isActive && (
              <Fragment>
                {checkoutContextConfig.ui.voucher.limit !== 0 && checkoutContextConfig.ui.voucher.limit <= vouchers.length && (
                  <MessageBox
                    type="warning"
                    message={formatMessage(messages.voucher_limit_message, { limit: checkoutContextConfig.ui.voucher.limit })}
                  />
                )}
                {isBlocked && (
                  <MessageBox
                    type="warning"
                    message={formatMessage(messages.voucher_locked_text)}
                  />
                )}
                {((!isBlocked) &&
                  ((checkoutContextConfig.ui.voucher.limit === 0) ||
                  (checkoutContextConfig.ui.voucher.limit > vouchers.length))) && (
                  <div className={Styles.inputWrapper}>
                    <div className={Styles.input}>
                      <TextInput
                        id={id}
                        type="text"
                        value={value}
                        variant="simple"
                        onChange={this.handleChange}
                        onKeyPress={this.handleKeyboardKeypress}
                        submitOnEnter={true}
                        autoFocus={(!isDefaultActive && isActive) || !this.state.isPristine}
                        label={formatMessage(messages.voucher_input_label)}
                        placeholder={formatMessage(messages.voucher_input_placeholder)}
                      />
                    </div>
                    <Button
                      align="left"
                      type="button"
                      label={formatMessage(messages.voucher_apply_button_label)}
                      variant="primary"
                      disabled={!this.state.value || this.state.value === ''}
                      onClick={this.addVoucher}
                      isLoading={isVoucherFetching}
                    />
                  </div>
                )}
              </Fragment>
            )}
            {voucherError && <FieldMessage error={true} message={formatMessage(messages.voucher_not_found)} />}
            {vouchers && vouchers.length > 0 && (
              <VoucherList removeLabel={formatMessage(messages.voucher_remove_button_label)} vouchers={vouchers} handleRemove={this.removeVoucher} />
            )}
          </div>
        ))}
      </CheckoutContextConfig.Consumer>
    );
  }
}

export default compose<React.ComponentClass<IOwnProps>>(
  injectIntl,
  connect<IConnectedProps, IConnectetDispatchProps>(
    (state: RootState) => ({
      vouchers: state.checkout.selection.discounts && state.checkout.selection.discounts.vouchers,
      voucherError: state.checkout.voucherError,
      isVoucherFetching: state.checkout.isVoucherFetching,
      voucherLockExpireDate: state.checkout.voucherLockExpireDate,
    }),
    {
      addVoucherRequest,
      removeVoucherRequest
    }
  ),
)(Voucher);
